import { responsiveFontSizes, createTheme } from "@mui/material/styles";

// Common design tokens shared across themes
const commonBreakpoints = {
  keys: ["xs", "sm", "md", "lg", "xl"],
  values: {
    xs: 300,
    sm: 650,
    md: 900,
    lg: 1200,
    xl: 1300,
  },
  unit: "px",
};

const commonTypography = {
  fontFamily: ["Poppins", "sans-serif"].join(","),
  fontSize: 14,
  fontSizeRegular: "12px !important",
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  cardTitle: {
    fontWeight: 700,
    fontSize: "1.5rem",
    lineHeight: 1.75,
    letterSpacing: "0.00938em",
  },
  h1: {
    fontWeight: 600,
    fontSize: "4rem",
    lineHeight: 1.167,
    letterSpacing: "-0.01562em",
  },
  h2: {
    fontWeight: 700,
    fontSize: "3.75rem",
    lineHeight: 1.2,
    letterSpacing: "-0.00833em",
  },
  h3: {
    fontWeight: 400,
    fontSize: "2.3rem",
    lineHeight: 1.167,
    letterSpacing: "0em",
  },
  h4: {
    fontWeight: 400,
    fontSize: "2.125rem",
    lineHeight: 1.235,
    letterSpacing: "0.00735em",
  },
  h5: {
    fontWeight: 600,
    fontSize: "1.4rem",
    lineHeight: 1.334,
    letterSpacing: "0em",
  },
  h6: {
    fontWeight: 400,
    fontSize: "1.25rem",
    lineHeight: 1.6,
    letterSpacing: "0.0075em",
  },
  HInsidePaper: {
    fontWeight: 400,
    fontSize: "1.15rem",
    lineHeight: 1.6,
    letterSpacing: "0.0075em",
  },
  subtitle1: {
    fontWeight: 500,
    fontSize: "0.9rem",
    lineHeight: 1.75,
    letterSpacing: "0.00938em",
  },
  subtitle2: {
    fontWeight: 500,
    fontSize: "0.9rem",
    lineHeight: 1.57,
    letterSpacing: "0.00714em",
  },
  body1: {
    fontWeight: 400,
    fontSize: "0.9rem",
    lineHeight: 1.7,
    letterSpacing: "0.00938em",
  },
  body2: {
    fontWeight: 400,
    fontSize: "0.9rem",
    lineHeight: 1.43,
    letterSpacing: "0.01071em",
  },
  button: {
    fontWeight: 500,
    fontSize: "0.9rem",
    lineHeight: 1.75,
    letterSpacing: "0.02857em",
    textTransform: "uppercase",
  },
  caption: {
    fontWeight: 400,
    fontSize: "0.75rem",
    lineHeight: 1.66,
    letterSpacing: "0.03333em",
  },
  overline: {
    fontWeight: 400,
    fontSize: "0.75rem",
    lineHeight: 2.66,
    letterSpacing: "0.08333em",
  },
};

const commonShadows = [
  "none",
  "0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)",
  "0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)",
  "0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)",
  "0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)",
  "0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)",
  "0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)",
  "0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)",
  "0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)",
  "0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)",
  "0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)",
  "0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)",
  "0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)",
  "0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)",
  "0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)",
  "0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)",
  "0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)",
  "0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)",
  "0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)",
  "0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)",
  "0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)",
  "0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)",
  "0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)",
  "0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)",
  "0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)",
];

const commonSharedOptions = {
  breakpoints: commonBreakpoints,
  typography: commonTypography,
  shadows: commonShadows,
  shape: {
    borderRadius: 5,
  },
  spacing: (factor) => `${0.5 * factor}rem`,
  transitions: {
    easing: {
      easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
      easeOut: "cubic-bezier(0.0, 0, 0.2, 1)",
      easeIn: "cubic-bezier(0.4, 0, 1, 1)",
      sharp: "cubic-bezier(0.4, 0, 0.6, 1)",
    },
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
  },
  zIndex: {
    propertyCard: 99,
    mobileStepper: 1000,
    speedDial: 1050,
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },
  borderRadius: {
    All: "8px",
    halfRound: "25%",
    round: "50%",
    roundCurves: "100px",
  },
  "@global": {
    "*::-webkit-scrollbar": {
      width: "0.2em",
    },
    "*::-webkit-scrollbar-track": {
      "-webkit-box-shadow": "inset 0 0 6px rgba(0,0,0,0.00)",
    },
    "*::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(0,0,0,.1)",
      outline: "1px solid slategrey",
    },
  },
};

const commonComponents = {
  MuiDataGrid: {
    defaultProps: {
      density: "standard",
    },
  },
  MuiChip: {
    defaultProps: {
      size: "small",
    },
    styleOverrides: {
      root: {},
      label: {
        fontSize: 12,
        fontWeight: 500,
      },
    },
  },
};

// ==========================================
// Theme 1: Existing Theme (Teal #298b8b)
// ==========================================
let rawTheme1 = createTheme({
  ...commonSharedOptions,
  palette: {
    common: {
      black: "#000",
      white: "#ffffff !important",
    },
    primary: {
      main: "#298b8b !important",
      contrastText: "#fff",
      opacity: "RGBA(52,128,250,0.15)",
      delete: "#dc143c !important",
      edit: "#0e90d9 !important",
      green: "#53c41a !important",
      light: "#e6f7f7 !important",
    },
    secondary: {
      main: "#353535 !important",
      contrastText: "#fff",
    },
    background: {
      main: "#f2f2f2 !important",
      default: "#f2f2f2",
      paper: "#ffffff",
      contrastText: "#fff !important",
    },
    BackgroundGradient: {
      main: "linear-gradient(to bottom, #ffffff , #f2f2f2)",
    },
    white: {
      main: "#FFFFFF !important",
      contrastText: "#fff",
      transparent: "#FFFFFF10",
    },
    black: { main: "#041B15", contrastText: "#fff" },
    greenShades: {
      g92: "#81AF8F !important",
      g94: "#DDF6E5",
      g96: "#F0F4EF",
      g98: "#EAF9EE !important",
      g90: "#D5F4DE",
      contrastText: "#fff",
    },
    error: {
      main: "#d32f2f !important",
      light: "#ef5350",
      dark: "#c62828",
      contrastText: "#fff",
    },
    warning: {
      main: "#ED6C02",
      light: "#ff9800",
      dark: "#e65100",
      contrastText: "#fff",
    },
    info: {
      main: "#0288d1",
      light: "#03a9f4",
      dark: "#01579b",
      contrastText: "#fff",
    },
    success: {
      main: "#2e7d32",
      light: "#4caf50",
      dark: "#1b5e20",
      contrastText: "#fff",
    },
    blue: {
      main: "#0e90d9",
    },
    sidebar: {
      main: "#298b8b",
      contrastText: "#ffffff",
      selectedBg: "#f2f2f2",
      selectedColor: "#353535",
    },
    menu: {
      main: "#298b8b",
      paper: "#ffffff",
      contrastText: "#353535",
    },
    details: {
      main: "#298b8b",
    },
    grey: {
      50: "#fafafa",
      100: "#f5f5f5",
      200: "#eeeeee",
      300: "#e0e0e0",
      400: "#bdbdbd",
      500: "#6C727F",
      600: "#757575",
      700: "#616161",
      800: "#424242",
      900: "#212121",
      A100: "#f5f5f5",
      A200: "#eeeeee",
      A400: "#bdbdbd",
      A700: "#616161",
      G1: "#FF4646",
      G2: "#C9C9C9",
      B1: "",
    },
    contrastThreshold: 3,
    tonalOffset: 0.2,
    text: {
      primary: "rgba(0, 0, 0, 0.87)",
      secondary: "rgba(0, 0, 0, 0.6)",
      disabled: "rgba(0, 0, 0, 0.38)",
    },
    action: {
      active: "rgba(0, 0, 0, 0.54)",
      hover: "#c7c6c661",
      hoverOpacity: 0.04,
      selected: "rgba(0, 0, 0, 0.08)",
      selectedOpacity: 0.08,
      disabled: "rgba(0, 0, 0, 0.26)",
      disabledBackground: "rgba(0, 0, 0, 0.12)",
      disabledOpacity: 0.38,
      focus: "rgba(0, 0, 0, 0.12)",
      focusOpacity: 0.12,
      activatedOpacity: 0.12,
    },
  },
});

export const theme1 = responsiveFontSizes(
  createTheme(rawTheme1, {
    components: {
      ...commonComponents,
      MuiButton: {
        styleOverrides: {
          contained: {
            color: "#ffffff !important",
          },
          containedPrimary: {
            backgroundColor: "#298b8b",
            color: "#ffffff !important",
            "&:hover": {
              backgroundColor: "#207272",
            },
          },
          outlinedPrimary: {
            borderColor: "#298b8b !important",
            color: "#298b8b !important",
          },
          textPrimary: {
            color: "#298b8b",
          },
        },
      },
    },
  }),
);

// ==============================================================
// Theme 2: New Theme (Dark Gray #30312d, Gold #c59b27, Off-white #FAF9F6)
// ==============================================================
let rawTheme2 = createTheme({
  ...commonSharedOptions,
  palette: {
    common: {
      black: "#000",
      white: "#ffffff !important",
    },
    // Buttons and Details: Gold #c59b27
    primary: {
      main: "#c59b27 !important",
      contrastText: "#ffffff",
      opacity: "rgba(197, 155, 39, 0.15)",
      delete: "#dc143c !important",
      edit: "#0e90d9 !important",
      green: "#53c41a !important",
      light: "#faf5e8 !important",
    },
    // Sidebar and Menus: Dark gray #30312d
    secondary: {
      main: "#30312d !important",
      contrastText: "#ffffff",
    },
    // Background: Off-white #FAF9F6
    background: {
      main: "#FAF9F6 !important",
      default: "#FAF9F6",
      paper: "#ffffff",
      contrastText: "#30312d !important",
    },
    BackgroundGradient: {
      main: "linear-gradient(to bottom, #ffffff , #FAF9F6)",
    },
    white: {
      main: "#FFFFFF !important",
      contrastText: "#30312d",
      transparent: "#FFFFFF10",
    },
    black: { main: "#1f201d", contrastText: "#fff" },
    greenShades: {
      g92: "#81AF8F !important",
      g94: "#DDF6E5",
      g96: "#F0F4EF",
      g98: "#EAF9EE !important",
      g90: "#D5F4DE",
      contrastText: "#fff",
    },
    error: {
      main: "#d32f2f !important",
      light: "#ef5350",
      dark: "#c62828",
      contrastText: "#fff",
    },
    warning: {
      main: "#ED6C02",
      light: "#ff9800",
      dark: "#e65100",
      contrastText: "#fff",
    },
    info: {
      main: "#0288d1",
      light: "#03a9f4",
      dark: "#01579b",
      contrastText: "#fff",
    },
    success: {
      main: "#2e7d32",
      light: "#4caf50",
      dark: "#1b5e20",
      contrastText: "#fff",
    },
    blue: {
      main: "#0e90d9",
    },
    sidebar: {
      main: "#30312d", // Dark gray #30312d
      contrastText: "#ffffff",
      selectedBg: "#FAF9F6", // Off-white
      selectedColor: "#c59b27", // Gold
    },
    menu: {
      main: "#30312d", // Dark gray #30312d
      paper: "#30312d",
      contrastText: "#ffffff",
    },
    details: {
      main: "#c59b27", // Gold #c59b27
    },
    grey: {
      50: "#fafafa",
      100: "#f5f5f5",
      200: "#eeeeee",
      300: "#e0e0e0",
      400: "#bdbdbd",
      500: "#6C727F",
      600: "#757575",
      700: "#616161",
      800: "#424242",
      900: "#212121",
      A100: "#f5f5f5",
      A200: "#eeeeee",
      A400: "#bdbdbd",
      A700: "#616161",
      G1: "#FF4646",
      G2: "#C9C9C9",
      B1: "",
    },
    contrastThreshold: 3,
    tonalOffset: 0.2,
    text: {
      primary: "rgba(0, 0, 0, 0.87)",
      secondary: "rgba(0, 0, 0, 0.6)",
      disabled: "rgba(0, 0, 0, 0.38)",
    },
    action: {
      active: "rgba(0, 0, 0, 0.54)",
      hover: "rgba(197, 155, 39, 0.08)",
      hoverOpacity: 0.04,
      selected: "rgba(197, 155, 39, 0.12)",
      selectedOpacity: 0.08,
      disabled: "rgba(0, 0, 0, 0.26)",
      disabledBackground: "rgba(0, 0, 0, 0.12)",
      disabledOpacity: 0.38,
      focus: "rgba(197, 155, 39, 0.12)",
      focusOpacity: 0.12,
      activatedOpacity: 0.12,
    },
  },
});

export const theme2 = responsiveFontSizes(
  createTheme(rawTheme2, {
    components: {
      ...commonComponents,
      // Buttons: Gold #c59b27
      MuiButton: {
        styleOverrides: {
          contained: {
            color: "#ffffff !important",
          },
          containedPrimary: {
            backgroundColor: "#c59b27",
            color: "#ffffff !important",
            "&:hover": {
              backgroundColor: "#ad8720",
            },
          },
          outlinedPrimary: {
            borderColor: "#c59b27 !important",
            color: "#c59b27 !important",
            "&:hover": {
              borderColor: "#ad8720 !important",
              backgroundColor: "rgba(197, 155, 39, 0.08) !important",
            },
          },
          textPrimary: {
            color: "#c59b27",
            "&:hover": {
              backgroundColor: "rgba(197, 155, 39, 0.08)",
            },
          },
        },
      },
      // Menus: Dark gray #30312d with gold details
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: "#30312d",
            color: "#ffffff",
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: "#ffffff",
            "&:hover": {
              backgroundColor: "rgba(197, 155, 39, 0.18)",
              color: "#c59b27",
            },
            "&.Mui-selected": {
              backgroundColor: "rgba(197, 155, 39, 0.28)",
              color: "#c59b27",
              "&:hover": {
                backgroundColor: "rgba(197, 155, 39, 0.38)",
              },
            },
          },
        },
      },

      MuiPaginationItem: {
        styleOverrides: {
          root: {
            "&.Mui-selected": {
              backgroundColor: "#c59b27 !important",
              color: "#ffffff !important",
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            "&.Mui-selected": {
              color: "#c59b27 !important",
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: "#c59b27 !important",
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          barColorPrimary: {
            backgroundColor: "#c59b27 !important",
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            "&.Mui-checked": {
              color: "#c59b27 !important",
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            "&.Mui-checked": {
              color: "#c59b27 !important",
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            "&.Mui-checked": {
              color: "#c59b27",
              "& + .MuiSwitch-track": {
                backgroundColor: "#c59b27",
              },
            },
          },
        },
      },
    },
  }),
);

export const themes = {
  theme1,
  theme2,
};

export const themePalettes = {
  theme1: {
    id: "theme1",
    name: "Theme 1",
    sidebar: "#298b8b",
    menus: "#298b8b",
    buttons: "#298b8b",
    details: "#298b8b",
    background: "#f2f2f2",
    primary: "#298b8b",
    secondary: "#353535",
  },
  theme2: {
    id: "theme2",
    name: "Theme 2",
    sidebar: "#30312d",
    menus: "#30312d",
    buttons: "#c59b27",
    details: "#c59b27",
    background: "#FAF9F6",
    primary: "#c59b27",
    secondary: "#30312d",
  },
};

// ==========================================
// Global Theme Control & CSS Variable Helper
// ==========================================
let currentThemeMode = "theme1";
if (typeof window !== "undefined") {
  try {
    const saved = window.localStorage?.getItem("app_theme");
    if (saved === "theme1") {
      currentThemeMode = "theme1";
    } else if (saved === "theme2") {
      window.localStorage?.removeItem("app_theme");
      currentThemeMode = "theme1";
    }
  } catch (e) {
    // Ignore storage errors
  }
}

export const getTheme = (mode = currentThemeMode) => {
  return mode === "theme2" ? theme2 : theme1;
};

export const getCurrentThemeMode = () => currentThemeMode;

export const applyThemeCssVariables = (mode) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const body = document.body;
  const config = mode === "theme2" ? themePalettes.theme2 : themePalettes.theme1;

  root.setAttribute("data-theme", mode);
  if (body) {
    body.setAttribute("data-theme", mode);
    body.classList.remove("theme-theme1", "theme-theme2");
    body.classList.add(`theme-${mode}`);
    body.style.backgroundColor = config.background;
  }

  root.style.setProperty("--theme-sidebar-bg", config.sidebar);
  root.style.setProperty("--theme-menu-bg", config.menus);
  root.style.setProperty("--theme-button-bg", config.buttons);
  root.style.setProperty("--theme-button-hover", mode === "theme2" ? "#ad8720" : "#207272");
  root.style.setProperty("--theme-details", config.details);
  root.style.setProperty("--theme-bg", config.background);
  root.style.setProperty("--theme-primary", config.primary);
  root.style.setProperty("--theme-secondary", config.secondary);
};

export const setCurrentThemeMode = (mode) => {
  if (mode === "theme1" || mode === "theme2") {
    currentThemeMode = mode;
    if (typeof window !== "undefined") {
      try {
        window.localStorage?.setItem("app_theme", mode);
        window.__THEME__ = mode;
      } catch (e) {
        // Ignore storage errors
      }
      applyThemeCssVariables(mode);
      window.dispatchEvent(new CustomEvent("app-theme-changed", { detail: { theme: mode } }));
    }
  }
};

// Global control variable exposure on window
if (typeof window !== "undefined") {
  window.__THEME__ = currentThemeMode;
  window.__setTheme__ = setCurrentThemeMode;
  window.__toggleTheme__ = () => {
    const next = currentThemeMode === "theme1" ? "theme2" : "theme1";
    setCurrentThemeMode(next);
    return next;
  };
  applyThemeCssVariables(currentThemeMode);
}

// Proxy default export so static `import theme from './theme'` seamlessly reflects the active theme!
const defaultThemeProxy = new Proxy(theme1, {
  get(target, prop, receiver) {
    const active = currentThemeMode === "theme2" ? theme2 : theme1;
    const value = Reflect.get(active, prop, receiver);
    if (typeof value === "function") {
      return value.bind(active);
    }
    return value;
  },
  has(target, prop) {
    const active = currentThemeMode === "theme2" ? theme2 : theme1;
    return Reflect.has(active, prop);
  },
  ownKeys() {
    const active = currentThemeMode === "theme2" ? theme2 : theme1;
    return Reflect.ownKeys(active);
  },
  getOwnPropertyDescriptor(target, prop) {
    const active = currentThemeMode === "theme2" ? theme2 : theme1;
    return Reflect.getOwnPropertyDescriptor(active, prop);
  },
});

export default defaultThemeProxy;
